import type { MouseEvent } from 'react'
import type { ShowWithPrice } from '~/shared/interfaces'
import type { ViewRow } from '../../model/components/index.h'

import { CardAnimations } from "/app/libraries/arcana/src/blocks/Cards/index.ts"
import { TopVodSimpleCard, VodSimpleCard } from "/app/libraries/arcana/src/blocks/Cards/Templates/index.ts"

import { useUnit } from 'effector-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { resolveShowNewLabel } from '~/entities/shows'
import { isPaid } from '~/features/payment'
import * as pinLockFeature from '~/features/pin-lock'
import { addQuery } from '~/shared/routes'
import {
  VodCardHorizontalCoversMap,
  VodCardVerticalCoversMap,
} from '~/shared/ui/components/cardCovers'
import {
  getCardContentPadding,
  getCategoryId,
  getCollectionId,
  getOnlyFavorites,
} from '../lib/style'

export const TvShowCard = ({
  row,
  item,
  idx,
}: {
  row: ViewRow
  item: ShowWithPrice
  idx: number
}) => {
  const {
    landscapeImageUrl,
    portraitImageUrl,
    rented,
    locked,
    name,
    id,
    labelPrice,
    vertical,
    blockedByAcl,
    labels,
  } = item

  const {
    appearance: {
      style: { posterSize, posterOrientation, topContent },
      resource: { search },
      title,
    },
  } = row

  const { isLocked, intercept } = useUnit({
    isLocked: pinLockFeature.$isLockedFn,
    intercept: pinLockFeature.intercept,
  })

  const { t } = useTranslation()

  // Pin-locked posters already replace their whole content via BaseCard's
  // own `locked` handling, so no extra guard is needed here.
  const newLabel = resolveShowNewLabel({ labels }, t)

  const contentPadding = getCardContentPadding(posterSize)

  const categoryId = getCategoryId(search)
  const collectionId = getCollectionId(search)
  const onlyFavorites = getOnlyFavorites(search)

  const handleShowCardClick = useCallback(
    (event: MouseEvent<Element>) => {
      event.preventDefault()
      event.stopPropagation()
      intercept({
        id,
        locked,
        name,
        '<event>': event,
      })
    },
    [intercept, id, locked, name]
  )

  const posterUrl =
    posterOrientation === 'HORIZONTAL' ? landscapeImageUrl : portraitImageUrl

  const covers =
    posterOrientation === 'HORIZONTAL'
      ? VodCardHorizontalCoversMap
      : VodCardVerticalCoversMap

  const Card = topContent ? TopVodSimpleCard : VodSimpleCard
  const topNumber = idx + 1

  const link = useMemo(
    () =>
      addQuery(`/content/series/${id}`, {
        categoryId,
        collectionId,
        'only-favorites': onlyFavorites || undefined,
        categoryName: title,
      }),
    [id, categoryId, collectionId, onlyFavorites, title]
  )

  return (
    <a href={link}>
      <Card
        onClick={handleShowCardClick}
        childrenTransition={CardAnimations.FadeIn}
        lazyImage={false}
        type={posterOrientation}
        posterUrl={posterUrl}
        paid={isPaid(item)}
        rented={rented}
        locked={isLocked({ id, locked })}
        aclBlockedBy={Boolean(blockedByAcl)}
        title={name}
        purchaseTitle={labelPrice}
        contentPadding={contentPadding}
        covers={covers}
        verticalVideo={vertical}
        topNumber={topNumber}
        newLabel={newLabel}
      />
    </a>
  )
}
